import { PLATFORM_IMAGE_MAX, PLATFORM_IMAGE_TYPES } from '@base/shared';
import { ImageUp } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui.tsx';

/**
 * Lê uma imagem da plataforma (logo ou símbolo) como data URL, no tipo e no tamanho que o
 * servidor aceita. Usada na Configuração › Plataforma e no Estúdio de estilos.
 */
export function readPlatformImage(
  file: File,
): Promise<{ url: string } | { error: 'imageType' | 'imageSize' }> {
  if (!PLATFORM_IMAGE_TYPES.includes(file.type)) return Promise.resolve({ error: 'imageType' });
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.length > PLATFORM_IMAGE_MAX ? { error: 'imageSize' } : { url });
    };
    reader.onerror = () => resolve({ error: 'imageType' });
    reader.readAsDataURL(file);
  });
}

/** Uma imagem (logo ou símbolo): a atual (ou a padrão), trocar e voltar ao padrão. */
export function ImageField({
  label,
  hint,
  fallback,
  value,
  readOnly,
  onPick,
  onReset,
}: {
  label: string;
  hint?: string;
  fallback: string;
  value: string | null;
  readOnly: boolean;
  onPick: (file: File | undefined) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-2 text-sm">
      <span className="font-medium">{label}</span>
      <div className="flex h-20 items-center justify-center rounded-md border border-rule bg-sheet p-3">
        <img src={value ?? fallback} alt={label} className="max-h-full max-w-full object-contain" />
      </div>
      {hint && <span className="text-xs text-muted">{hint}</span>}
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <input
            ref={input}
            type="file"
            accept={PLATFORM_IMAGE_TYPES.join(',')}
            className="sr-only"
            tabIndex={-1}
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button variant="secondary" onClick={() => input.current?.click()}>
            <ImageUp size={16} aria-hidden />
            {t('platform.upload')}
          </Button>
          {value && (
            <Button variant="secondary" onClick={onReset}>
              {t('platform.useDefault')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
